import { z } from 'zod';
import { isoTimestamp } from './common';
import { modelIdSchema, providerTypeSchema } from './providers';
import { requestContextValuesSchema } from './prompts';
import { attachmentSchema, MAX_ATTACHMENTS_PER_MESSAGE } from './files';
import { sessionConfigSchema } from './sessions';
import { claudeBuiltinToolSchema, memoryConfigSchema } from './tools';

export const agentSlugSchema = z
  .string()
  .trim()
  .regex(
    /^[a-z0-9][a-z0-9-]{0,62}$/,
    'Use 1–63 lowercase letters, digits or dashes, starting with a letter or digit',
  );
const agentNameSchema = z.string().trim().min(1, 'Name is required').max(100);
const descriptionSchema = z.string().trim().max(1000);

export const agentStatusSchema = z.enum(['draft', 'active', 'archived']);
export type AgentStatus = z.infer<typeof agentStatusSchema>;

/** `stateful`: thread memory; `sessions`: session levels (see `sessionConfigSchema`). */
export const memoryModeSchema = z.enum(['stateless', 'stateful', 'sessions']);
export type MemoryMode = z.infer<typeof memoryModeSchema>;

export const systemPromptSchema = z.string().max(100_000, 'System prompt is too long');

/**
 * How much the model reasons before it answers. Each provider maps the level to its own setting
 * (Anthropic `effort`, OpenAI `reasoning_effort`, Gemini thinking level or budget); levels a model
 * doesn't support are rounded to the nearest one it does. Unset uses the provider default.
 */
export const REASONING_EFFORTS = [
  'none',
  'minimal',
  'low',
  'medium',
  'high',
  'xhigh',
  'max',
] as const;
export const reasoningEffortSchema = z.enum(REASONING_EFFORTS);
export type ReasoningEffort = z.infer<typeof reasoningEffortSchema>;

export const modelParamsSchema = z
  .object({
    temperature: z.number().min(0).max(2).optional(),
    topP: z.number().min(0).max(1).optional(),
    maxTokens: z.number().int().min(1).max(200_000).optional(),
    effort: reasoningEffortSchema.optional(),
    /** Prompt caching for this agent's model calls; on unless set to false. */
    promptCaching: z.boolean().optional(),
  })
  .strict();
export type ModelParams = z.infer<typeof modelParamsSchema>;

export const agentLimitsSchema = z
  .object({
    maxSteps: z.number().int().min(1).max(50),
    maxDurationMs: z.number().int().min(1_000).max(600_000),
    maxOutputTokens: z.number().int().min(1).max(200_000).optional(),
  })
  .strict();
export type AgentLimits = z.infer<typeof agentLimitsSchema>;

export const DEFAULT_LIMITS: AgentLimits = { maxSteps: 10, maxDurationMs: 120_000 };
/** Upper bound of any run; the orphan sweeper uses it. */
export const MAX_RUN_DURATION_MS = 600_000;

export const agentVersionDtoSchema = z.object({
  id: z.uuid(),
  agentId: z.uuid(),
  version: z.number().int(),
  frozen: z.boolean(),
  systemPrompt: z.string(),
  providerConnectionId: z.uuid().nullable(),
  providerType: providerTypeSchema.nullable(),
  model: z.string().nullable(),
  params: modelParamsSchema,
  memoryMode: memoryModeSchema,
  memoryConfig: memoryConfigSchema,
  sessionConfig: sessionConfigSchema,
  limits: agentLimitsSchema,
  toolIds: z.array(z.uuid()),
  mcpServerIds: z.array(z.uuid()),
  skillIds: z.array(z.uuid()),
  /** Workflows the agent can start (each is a tool). */
  workflowIds: z.array(z.uuid()),
  /** Knowledge workspaces the agent can search. */
  workspaceIds: z.array(z.uuid()),
  claudeBuiltinTools: z.array(claudeBuiltinToolSchema),
  /** Gives the agent the platform's `web_search` and `web_fetch` tools. */
  webSearch: z.boolean(),
  publishedAt: isoTimestamp.nullable(),
  createdAt: isoTimestamp,
  updatedAt: isoTimestamp,
});
export type AgentVersionDto = z.infer<typeof agentVersionDtoSchema>;

export const agentVersionSummarySchema = agentVersionDtoSchema.pick({
  id: true,
  version: true,
  frozen: true,
  model: true,
  providerType: true,
  publishedAt: true,
  createdAt: true,
});
export type AgentVersionSummary = z.infer<typeof agentVersionSummarySchema>;

export const agentDtoSchema = z.object({
  id: z.uuid(),
  slug: z.string(),
  name: z.string(),
  description: z.string(),
  status: agentStatusSchema,
  draftVersionId: z.uuid(),
  publishedVersionId: z.uuid().nullable(),
  publishedVersion: z.number().int().nullable(),
  model: z.string().nullable(),
  createdAt: isoTimestamp,
  updatedAt: isoTimestamp,
});
export type AgentDto = z.infer<typeof agentDtoSchema>;

export const agentDetailDtoSchema = agentDtoSchema.extend({
  draft: agentVersionDtoSchema,
  published: agentVersionDtoSchema.nullable(),
});
export type AgentDetailDto = z.infer<typeof agentDetailDtoSchema>;

export const createAgentRequestSchema = z.object({
  name: agentNameSchema,
  slug: agentSlugSchema,
  description: descriptionSchema.optional(),
});
export type CreateAgentRequest = z.infer<typeof createAgentRequestSchema>;

export const updateAgentRequestSchema = z
  .object({ name: agentNameSchema.optional(), description: descriptionSchema.optional() })
  .refine((v) => Object.keys(v).length > 0, { message: 'Nothing to update' });
export type UpdateAgentRequest = z.infer<typeof updateAgentRequestSchema>;

export const updateDraftRequestSchema = z
  .object({
    systemPrompt: systemPromptSchema.optional(),
    providerConnectionId: z.uuid().nullable().optional(),
    model: modelIdSchema.nullable().optional(),
    params: modelParamsSchema.optional(),
    limits: agentLimitsSchema.optional(),
    memoryMode: memoryModeSchema.optional(),
    memoryConfig: memoryConfigSchema.optional(),
    sessionConfig: sessionConfigSchema.optional(),
    toolIds: z.array(z.uuid()).max(100).optional(),
    mcpServerIds: z.array(z.uuid()).max(20).optional(),
    skillIds: z.array(z.uuid()).max(50).optional(),
    workflowIds: z.array(z.uuid()).max(20).optional(),
    workspaceIds: z.array(z.uuid()).max(20).optional(),
    claudeBuiltinTools: z.array(claudeBuiltinToolSchema).max(20).optional(),
    webSearch: z.boolean().optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'Nothing to update' });
export type UpdateDraftRequest = z.infer<typeof updateDraftRequestSchema>;

// --- Runs ---

export const runStatusSchema = z.enum(['running', 'succeeded', 'failed', 'cancelled', 'timeout']);
export type RunStatus = z.infer<typeof runStatusSchema>;

export const runSourceSchema = z.enum(['gateway', 'playground', 'workflow']);
export type RunSource = z.infer<typeof runSourceSchema>;

export const RUN_ERROR_CODES = [
  'provider_auth_failed',
  'provider_unavailable',
  'run_timeout',
  'cancelled',
  'invalid_request',
  'internal',
] as const;
export const runErrorCodeSchema = z.enum(RUN_ERROR_CODES);
export type RunErrorCode = z.infer<typeof runErrorCodeSchema>;

export const runErrorSchema = z.object({ code: runErrorCodeSchema, message: z.string() });
export type RunError = z.infer<typeof runErrorSchema>;

export const chatMessageSchema = z
  .object({
    role: z.enum(['user', 'assistant']),
    content: z.string().max(100_000),
    /** Files and images (stored files; see `POST /files`). User messages only. */
    attachments: z.array(attachmentSchema).max(MAX_ATTACHMENTS_PER_MESSAGE).optional(),
  })
  .refine((m) => m.content.trim().length > 0 || (m.attachments?.length ?? 0) > 0, {
    message: 'Write a message or attach a file',
    path: ['content'],
  })
  .refine((m) => m.role === 'user' || !m.attachments?.length, {
    message: 'Only user messages carry attachments',
    path: ['attachments'],
  });
export type ChatMessage = z.infer<typeof chatMessageSchema>;

export const runUsageSchema = z.object({
  inputTokens: z.number().int().nonnegative(),
  outputTokens: z.number().int().nonnegative(),
  cacheReadTokens: z.number().int().nonnegative().optional(),
  cacheWriteTokens: z.number().int().nonnegative().optional(),
  /** Priced from the model catalog at run end; null when the model has no known price. */
  costUsd: z.number().nonnegative().nullable().optional(),
});
export type RunUsage = z.infer<typeof runUsageSchema>;

export const runDtoSchema = z.object({
  id: z.uuid(),
  agentId: z.uuid(),
  versionId: z.uuid(),
  version: z.number().int(),
  source: runSourceSchema,
  status: runStatusSchema,
  userId: z.uuid().nullable(),
  /** The conversation the run belongs to (client-sent or generated), if any. */
  threadId: z.string().nullable(),
  apiKeyId: z.uuid().nullable(),
  providerType: providerTypeSchema,
  model: z.string(),
  output: z.object({ text: z.string() }).nullable(),
  error: runErrorSchema.nullable(),
  startedAt: isoTimestamp,
  endedAt: isoTimestamp.nullable(),
  latencyMs: z.number().int().nullable(),
  inputTokens: z.number().int().nullable(),
  outputTokens: z.number().int().nullable(),
  cacheReadTokens: z.number().int().nullable(),
  cacheWriteTokens: z.number().int().nullable(),
  /** Priced from the model catalog at run end; null when the model has no known price. */
  costUsd: z.number().nullable(),
});
export type RunDto = z.infer<typeof runDtoSchema>;

export const runStepTypeSchema = z.enum([
  'model_call',
  'tool_call',
  'tool_result',
  'mcp_call',
  'mapping',
  'error',
]);
export type RunStepType = z.infer<typeof runStepTypeSchema>;

export const runStepDtoSchema = z.object({
  id: z.uuid(),
  seq: z.number().int(),
  type: runStepTypeSchema,
  name: z.string(),
  payload: z.record(z.string(), z.unknown()),
  startedAt: isoTimestamp,
  endedAt: isoTimestamp.nullable(),
});
export type RunStepDto = z.infer<typeof runStepDtoSchema>;

export const runDetailDtoSchema = runDtoSchema.extend({
  input: z.object({
    messages: z.array(chatMessageSchema),
    requestContext: z.record(z.string(), z.unknown()).optional(),
  }),
  steps: z.array(runStepDtoSchema),
});
export type RunDetailDto = z.infer<typeof runDetailDtoSchema>;

export const runListQuerySchema = z.object({
  cursor: z.uuid().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  agentId: z.uuid().optional(),
  status: runStatusSchema.optional(),
  source: runSourceSchema.optional(),
  /** Inclusive lower / exclusive upper bound on the start time. */
  from: z.iso.datetime({ offset: true }).optional(),
  to: z.iso.datetime({ offset: true }).optional(),
});
export type RunListQuery = z.infer<typeof runListQuerySchema>;

// --- Threads ---

/**
 * A conversation: the runs of one agent that share a thread id and a caller (two API keys may use
 * the same thread id for different conversations).
 */
export const threadCallerSchema = z.object({
  type: z.enum(['api_key', 'user']),
  id: z.uuid(),
  /** The API key's name or the user's email (null once deleted). */
  name: z.string().nullable(),
});
export type ThreadCaller = z.infer<typeof threadCallerSchema>;

export const threadDtoSchema = z.object({
  agentId: z.uuid(),
  threadId: z.string(),
  caller: threadCallerSchema.nullable(),
  /** Source of the latest run. */
  source: runSourceSchema,
  runCount: z.number().int(),
  firstAt: isoTimestamp,
  lastAt: isoTimestamp,
  lastStatus: runStatusSchema,
  inputTokens: z.number().int(),
  outputTokens: z.number().int(),
  /** Total cost of the priced runs. */
  costUsd: z.number(),
  /** Runs whose model had no known price, so they are missing from `costUsd`. */
  unpricedRuns: z.number().int(),
  /** The first user message. */
  preview: z.string(),
  /** Id of the latest run (the list cursor). */
  lastRunId: z.uuid(),
});
export type ThreadDto = z.infer<typeof threadDtoSchema>;

export const threadListQuerySchema = z.object({
  cursor: z.uuid().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  agentId: z.uuid().optional(),
  source: runSourceSchema.optional(),
});
export type ThreadListQuery = z.infer<typeof threadListQuerySchema>;

export const threadDetailQuerySchema = z.object({
  agentId: z.uuid(),
  threadId: z.string().min(1).max(200),
  /** The caller: exactly one of them (or neither, for runs that have none). */
  apiKeyId: z.uuid().optional(),
  userId: z.uuid().optional(),
});
export type ThreadDetailQuery = z.infer<typeof threadDetailQuerySchema>;

/** One turn of the conversation: the run with the messages it was sent (steps load per run). */
export const threadRunDtoSchema = runDtoSchema.extend({
  input: z.object({ messages: z.array(chatMessageSchema) }),
});
export type ThreadRunDto = z.infer<typeof threadRunDtoSchema>;

export const threadDetailDtoSchema = z.object({
  thread: threadDtoSchema,
  /** Oldest first (at most `MAX_THREAD_RUNS`, the latest ones). */
  runs: z.array(threadRunDtoSchema),
});
export type ThreadDetailDto = z.infer<typeof threadDetailDtoSchema>;
export const MAX_THREAD_RUNS = 200;

// --- Playground ---

export const playgroundRequestSchema = z.object({
  /** `draft`, `published`, or a specific version id. */
  version: z.union([z.literal('draft'), z.literal('published'), z.uuid()]).default('draft'),
  /** Stateful agents remember the thread; the playground then sends only the new message. */
  threadId: z.string().min(1).max(200).optional(),
  /** Fills `{{key}}` variables in the system prompt (Mastra-style request context). */
  requestContext: requestContextValuesSchema.optional(),
  /**
   * Request headers to run through the agent's endpoint mapping, exactly as the gateway would
   * (MCP `_meta`/headers, tool variables, prompt variables, env). Omit to skip the mapping.
   */
  headers: z
    .record(z.string().min(1).max(200), z.string().max(16_384))
    .refine((h) => Object.keys(h).length <= 50, { message: 'At most 50 headers' })
    .optional(),
  messages: z
    .array(chatMessageSchema)
    .min(1)
    .max(200)
    .refine((m) => m.at(-1)?.role === 'user', {
      message: 'The last message must be from the user',
    }),
});
export type PlaygroundRequest = z.infer<typeof playgroundRequestSchema>;

/** Server-sent events of a run, as sent to the playground (and later the gateway). */
export const runEventSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('run.started'),
    runId: z.uuid(),
    version: z.number().int(),
    /** Session levels this run used (memory mode `sessions`). */
    sessions: z
      .array(z.object({ level: z.string(), key: z.string(), sessionId: z.uuid() }))
      .optional(),
  }),
  z.object({ type: z.literal('text.delta'), text: z.string() }),
  z.object({
    type: z.literal('tool.call'),
    id: z.string(),
    name: z.string(),
    args: z.unknown(),
  }),
  z.object({ type: z.literal('tool.result'), id: z.string(), ok: z.boolean() }),
  z.object({
    type: z.literal('run.completed'),
    runId: z.uuid(),
    output: z.object({ text: z.string() }),
    usage: runUsageSchema,
    latencyMs: z.number().int(),
  }),
  z.object({
    type: z.literal('run.error'),
    runId: z.uuid().nullable(),
    code: runErrorCodeSchema,
    message: z.string(),
  }),
]);
export type RunEventDto = z.infer<typeof runEventSchema>;
